import { useEffect, useState, type ComponentType } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { userLogout } from '@/api/service/auth';
import { useAppContext } from '@/contexts/AppContext';
import { LogoutLoader } from './LogoutLoader';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { cn } from './ui/utils';
import {
  ChevronLeft,
  ChevronRight,
  CreditCard,
  LayoutDashboard,
  LogOut,
  Users,
} from 'lucide-react';

const LOGO_URL =
  'https://media.istockphoto.com/id/1423550966/vector/profit-rounded-lines-icon.jpg?s=612x612&w=0&k=20&c=_KFEK2PUIlquKGVUYQ18I2rO6xQ3ieFDEx-xHpXRLTI=';

const SIDEBAR_COLLAPSED_KEY = 'pms-sidebar-collapsed';

const menuItems = [
  { id: 'dashboard', path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'members', path: '/members', label: 'Members', icon: Users },
  { id: 'payment', path: '/payment', label: 'Payment', icon: CreditCard },
] as const;

function NavItem({
  collapsed,
  isActive,
  label,
  icon: Icon,
  onClick,
}: {
  collapsed: boolean;
  isActive: boolean;
  label: string;
  icon: ComponentType<{ className?: string }>;
  onClick: () => void;
}) {
  const button = (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full cursor-pointer items-center rounded-lg text-sm font-medium transition-all',
        collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5',
        isActive ? 'bg-[#4369a0] text-white shadow-sm' : 'text-gray-700 hover:bg-gray-50'
      )}
    >
      <Icon className={cn('h-4 w-4 shrink-0', isActive ? 'text-white' : 'text-gray-500')} />
      {!collapsed && <span className="truncate">{label}</span>}
      {!collapsed && isActive && (
        <span className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
      )}
    </button>
  );

  if (!collapsed) {
    return button;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsAuthenticated } = useAppContext();
  const [collapsed, setCollapsed] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  useEffect(() => {
    setCollapsed(localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true');
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await userLogout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setTimeout(() => {
        localStorage.removeItem('accessToken');
        setIsAuthenticated(false);
        setIsLoggingOut(false);
        navigate('/login', { replace: true });
      }, 1000);

    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'flex h-screen shrink-0 flex-col border-r border-gray-100 bg-white shadow-xs transition-[width] duration-200 ease-in-out',
          collapsed ? 'w-[68px]' : 'w-64'
        )}
      >
        {/* Header */}
        <div
          className={cn(
            'relative overflow-hidden border-b border-[#4369a0]/10',
            collapsed ? 'px-2 py-3' : 'px-3 py-4'
          )}
        >
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#4369a0]/10 via-[#4369a0]/5 to-transparent" />
          <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-[#4369a0]/10 blur-2xl" />

          <div className={cn('relative z-10', collapsed && 'flex justify-center')}>
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4369a0]/30"
                  >
                    <div className="rounded-full bg-white p-0.5 shadow-sm ring-2 ring-[#4369a0]/15">
                      <img
                        src={LOGO_URL}
                        alt="PMS Logo"
                        className="h-9 w-9 rounded-full border border-gray-100 object-cover"
                      />
                    </div>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8} className="max-w-[200px] text-center">
                  <p className="font-semibold tracking-[0.12em]">PMS</p>
                  <p className="text-primary-foreground/80">பொருளாதார முன்னேற்ற சங்கம்</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4369a0]/25 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white p-0.5 shadow-sm ring-2 ring-[#4369a0]/15">
                    <img
                      src={LOGO_URL}
                      alt="PMS Logo"
                      className="h-10 w-10 rounded-full border border-gray-100 object-cover"
                    />
                  </div>
                  <div className="flex h-11 min-w-0 flex-col justify-center pt-1">
                    <p className="text-[10px] font-semibold uppercase leading-none tracking-[0.18em] text-[#4369a0]/80">
                      Admin Portal
                    </p>
                    <h2 className="mt-1 text-lg font-bold leading-none tracking-[0.14em] text-gray-900">
                      PMS
                    </h2>
                  </div>
                </div>

                <div className="mt-0 border-none border-[#4369a0]/10 pt-2">
                  <div className="flex items-center justify-center gap-1.5">
                    <span className="h-1 w-1 shrink-0 rounded-full bg-[#4369a0]" />
                    <p className="whitespace-nowrap text-xs font-medium leading-tight text-gray-700">
                      பொருளாதார முன்னேற்ற சங்கம்
                    </p>
                    <span className="h-1 w-1 shrink-0 rounded-full bg-[#4369a0]" />
                  </div>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-2 scrollbar-hide">
          {menuItems.map((item) => (
            <NavItem
              key={item.id}
              collapsed={collapsed}
              isActive={location.pathname === item.path}
              label={item.label}
              icon={item.icon}
              onClick={() => navigate(item.path)}
            />
          ))}
        </nav>

        {/* Collapse toggle */}
        <div className={cn('px-2', collapsed ? 'pb-1' : 'pb-2')}>
          <button
            type="button"
            onClick={toggleCollapsed}
            className={cn(
              'flex w-full cursor-pointer items-center rounded-lg border border-gray-200/80 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700',
              collapsed ? 'justify-center py-2' : 'gap-2 px-3 py-2 text-xs font-medium'
            )}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>

        {/* Admin footer */}
        <div className={cn('m-2 rounded-lg bg-gray-50', collapsed ? 'p-2' : 'p-2.5')}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <img
                    src={LOGO_URL}
                    alt="Admin Profile"
                    className="h-8 w-8 rounded-full border border-gray-300 object-cover"
                  />
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>
                  <p className="font-medium">Perumal B</p>
                  <p className="text-primary-foreground/80">Admin</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="cursor-pointer rounded-md p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8}>
                  Logout
                </TooltipContent>
              </Tooltip>
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              <img
                src={LOGO_URL}
                alt="Admin Profile"
                className="h-9 w-9 shrink-0 rounded-full border border-gray-300 object-cover"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900">Perumal B</p>
                <p className="text-xs text-gray-500">Admin</p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="shrink-0 cursor-pointer rounded-md p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  Logout
                </TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </aside>
      {isLoggingOut && <LogoutLoader />}
    </TooltipProvider>
  );
}
